import { FormEvent, useState } from 'react';
import { FiEye, FiEyeOff, FiLogIn, FiMail } from 'react-icons/fi';
import { EmailVerificationRequired, useAuthStore } from '../stores/authStore';

const fields = { width: '100%', minHeight: 47, padding: '12px 14px', borderRadius: 12, border: '1px solid #2c342e', background: '#111511', color: '#f5f6f7', fontSize: 14, outlineColor: '#19EA4D' } as const;

export function LoginScreen() {
  const { signIn, signUp, verifyEmail, resendVerification } = useAuthStore();
  const [register, setRegister] = useState(false);
  const [verificationMode, setVerificationMode] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [notice, setNotice] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setBusy(true); setError('');
    const data = new FormData(event.currentTarget);
    try {
      if (verificationMode) {
        const result = await verifyEmail(verificationEmail, String(data.get('verificationCode') || ''));
        if (!result.verified) throw new Error('Não foi possível confirmar o e-mail. Confira o código e tente novamente.');
        setVerificationMode(false);
        setVerificationCode('');
        setRegister(false);
        setNotice('E-mail confirmado. Agora entre com seu usuário e senha.');
        return;
      }
      const password = String(data.get('password') || '');
      if (register) {
        if (password.length < 8) throw new Error('A senha precisa ter pelo menos 8 caracteres.');
        if (password !== data.get('confirmPassword')) throw new Error('As senhas não coincidem.');
        const age = Number(data.get('age'));
        if (!Number.isInteger(age) || age < 18) throw new Error('É necessário ter 18 anos ou mais.');
        const email = String(data.get('email') || '').trim();
        const result = await signUp({
          firstName: String(data.get('firstName') || '').trim(), lastName: String(data.get('lastName') || '').trim(),
          username: String(data.get('username') || '').trim(), email, age,
          sex: String(data.get('sex') || ''), password,
        });
        if (result.verificationRequired) {
          setVerificationEmail(result.email || email);
          setVerificationMode(true);
          setRegister(false);
          setNotice('Enviamos um código de 6 dígitos para seu e-mail. Ele vale por 15 minutos.');
          return;
        }
        if (!result.token) throw new Error(result.error || 'Não foi possível iniciar a sessão.');
      } else {
        await signIn(String(data.get('identifier') || ''), password);
      }
    } catch (e) {
      if (e instanceof EmailVerificationRequired) {
        setVerificationEmail(e.email);
        setVerificationMode(true);
        setNotice('Confirme seu e-mail para acessar a conta.');
      } else setError((e as Error).message || 'Não foi possível entrar.');
    }
    finally { setBusy(false); }
  };

  return <main className="login-screen">
    <div className="login-orb login-orb-one" /><div className="login-orb login-orb-two" />
    <section className="login-content">
      <img className="login-wordmark" src="/rush-props-logo.png" alt="Rush Props" />
      <p className="login-tagline">Seu green, seu lucro</p>
      <form className="login-card" onSubmit={submit}>
        <span className="login-kicker">{verificationMode ? 'CONFIRME SEU E-MAIL' : register ? 'CRIAR CONTA' : 'BEM-VINDO'}</span>
        <h1>{verificationMode ? 'Só falta confirmar' : register ? 'Seus dados' : 'Acesse sua conta'}</h1>
        {verificationMode && <>
          <p>Enviamos um código para <b>{verificationEmail}</b>. Informe os 6 dígitos para ativar sua conta.</p>
          <label>CÓDIGO DE CONFIRMAÇÃO<input style={fields} name="verificationCode" inputMode="numeric" autoComplete="one-time-code" placeholder="000000" maxLength={6} value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required /></label>
        </>}
        {!verificationMode && register && <>
          <label>NOME<input style={fields} name="firstName" placeholder="Seu nome" autoComplete="given-name" required /></label>
          <label>SOBRENOME<input style={fields} name="lastName" placeholder="Seu sobrenome" autoComplete="family-name" required /></label>
          <label>USUÁRIO<input style={fields} name="username" placeholder="Seu usuário" autoComplete="username" required /></label>
          <label>IDADE<input style={fields} name="age" placeholder="18 ou mais" type="number" min="18" required /></label>
          <label>SEXO<select style={fields} name="sex" defaultValue="" required><option value="" disabled>Selecione</option><option>Feminino</option><option>Masculino</option><option>Outro</option><option>Prefiro não informar</option></select></label>
          <label>E-MAIL<input style={fields} name="email" placeholder="voce@exemplo.com" type="email" autoComplete="email" required /></label>
        </>}
        {!verificationMode && !register && <label>USUÁRIO<input style={fields} name="identifier" placeholder="Seu usuário ou e-mail" autoComplete="username" required /></label>}
        {!verificationMode && <label>SENHA<span className="login-password"><input style={fields} name="password" placeholder="Sua senha" type={showPassword ? 'text' : 'password'} autoComplete={register ? 'new-password' : 'current-password'} minLength={register ? 8 : undefined} required /><button type="button" aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'} onClick={() => setShowPassword(!showPassword)}>{showPassword ? <FiEyeOff /> : <FiEye />}</button></span></label>}
        {!verificationMode && register && <label>CONFIRMAÇÃO DE SENHA<input style={fields} name="confirmPassword" placeholder="Digite a senha novamente" type="password" autoComplete="new-password" required /></label>}
        {error && <div className="login-error" role="alert">{error}</div>}
        {notice && <div role="status">{notice}</div>}
        <button className="login-submit" type="submit" disabled={busy}><FiLogIn />{busy ? 'AGUARDE…' : verificationMode ? 'CONFIRMAR E-MAIL' : register ? 'CRIAR CONTA' : 'ENTRAR'}</button>
        {verificationMode ? <>
          <button className="login-switch" type="button" disabled={busy} onClick={async () => { setBusy(true); setError(''); try { await resendVerification(verificationEmail); setNotice('Se houver um cadastro pendente para este e-mail, um novo código será enviado. Aguarde um minuto para solicitar outro.'); } catch (e) { setError((e as Error).message); } finally { setBusy(false); } }}>Reenviar código</button>
          <button className="login-switch" type="button" onClick={() => { setVerificationMode(false); setRegister(false); setNotice(''); setError(''); }}>Voltar ao login</button>
        </> : <button className="login-switch" type="button" onClick={() => { setRegister(!register); setError(''); setNotice(''); }}>{register ? 'Já tem conta? Entrar' : 'Ainda não tem conta? Criar conta'}</button>}
      </form>
    </section>
  </main>;
}
